// ============================================================================
// recsuit-workflow service — typed client for /api/v1/workflow/*
// ============================================================================
// Allowed dependencies: shared axios client only. Must NOT import any
// recsuit-domain code (services, types, endpoints, screens).

import { get, post, put, del } from 'api/client/axiosClient';
import { workflowEndpoints } from './endpoints';
import type {
  ApprovalInboxItem,
  ApprovalResolution,
  CanTriggerResult,
  ChainTreeNode,
  CreateDefinitionRequest,
  CreateSpawnRuleRequest,
  CreateStateRequest,
  CreateTransitionRequest,
  DefinitionFullDetail,
  ResolveApprovalRequest,
  StartInstanceRequest,
  TriggerRequest,
  TriggerResult,
  UpdateDefinitionRequest,
  UpdateStateRequest,
  UpdateTransitionRequest,
  WorkflowDefinition,
  WorkflowHistory,
  WorkflowInstance,
  WorkflowInstanceDetail,
  WorkflowSpawnRule,
  WorkflowState,
  WorkflowTransition,
} from './types';

// The backend returns plain objects (no ApiResponse wrapper for the workflow
// controllers — they return Ok(model) directly). axios client `get<T>` returns
// AxiosResponse<T>, so .data is the model.
async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  const response = await promise;
  return response.data;
}

class WorkflowService {
  // ----- Definitions -----
  listDefinitions(publishedOnly = false) {
    return unwrap(get<WorkflowDefinition[]>(workflowEndpoints.listDefinitions(publishedOnly)));
  }
  getDefinitionFull(id: string) {
    return unwrap(get<DefinitionFullDetail>(workflowEndpoints.getDefinitionFull(id)));
  }
  getLatestPublished(code: string) {
    return unwrap(get<WorkflowDefinition>(workflowEndpoints.getLatestPublished(code)));
  }
  createDefinition(req: CreateDefinitionRequest) {
    return unwrap(post<WorkflowDefinition>(workflowEndpoints.createDefinition(), req));
  }
  updateDefinition(id: string, req: UpdateDefinitionRequest) {
    return unwrap(put<WorkflowDefinition>(workflowEndpoints.updateDefinition(id), req));
  }
  publishDefinition(id: string) {
    return unwrap(post<WorkflowDefinition>(workflowEndpoints.publishDefinition(id), {}));
  }
  deleteDefinition(id: string) {
    return unwrap(del<void>(workflowEndpoints.deleteDefinition(id)));
  }

  // ----- States -----
  addState(defId: string, req: CreateStateRequest) {
    return unwrap(post<WorkflowState>(workflowEndpoints.addState(defId), req));
  }
  updateState(stateId: string, req: UpdateStateRequest) {
    return unwrap(put<WorkflowState>(workflowEndpoints.updateState(stateId), req));
  }
  deleteState(stateId: string) {
    return unwrap(del<void>(workflowEndpoints.deleteState(stateId)));
  }

  // ----- Transitions -----
  addTransition(defId: string, req: CreateTransitionRequest) {
    return unwrap(post<WorkflowTransition>(workflowEndpoints.addTransition(defId), req));
  }
  updateTransition(transitionId: string, req: UpdateTransitionRequest) {
    return unwrap(put<WorkflowTransition>(workflowEndpoints.updateTransition(transitionId), req));
  }
  deleteTransition(transitionId: string) {
    return unwrap(del<void>(workflowEndpoints.deleteTransition(transitionId)));
  }

  // ----- Spawn rules -----
  addSpawnRule(defId: string, req: CreateSpawnRuleRequest) {
    return unwrap(post<WorkflowSpawnRule>(workflowEndpoints.addSpawnRule(defId), req));
  }
  deleteSpawnRule(ruleId: string) {
    return unwrap(del<void>(workflowEndpoints.deleteSpawnRule(ruleId)));
  }

  // ----- Instances -----
  startInstance(req: StartInstanceRequest) {
    return unwrap(post<WorkflowInstance>(workflowEndpoints.startInstance(), req));
  }
  getInstance(id: string) {
    return unwrap(get<WorkflowInstanceDetail>(workflowEndpoints.getInstance(id)));
  }
  getInstanceByEntity(entityType: string, entityId: string) {
    return unwrap(get<WorkflowInstance>(workflowEndpoints.getInstanceByEntity(entityType, entityId)));
  }
  trigger(instanceId: string, code: string, body?: TriggerRequest) {
    return unwrap(post<TriggerResult>(workflowEndpoints.trigger(instanceId, code), body ?? {}));
  }
  canTrigger(instanceId: string, code: string) {
    return unwrap(get<CanTriggerResult>(workflowEndpoints.canTrigger(instanceId, code)));
  }
  history(instanceId: string) {
    return unwrap(get<WorkflowHistory[]>(workflowEndpoints.history(instanceId)));
  }

  // ----- Approvals -----
  approvalInbox() {
    return unwrap(get<ApprovalInboxItem[]>(workflowEndpoints.approvalInbox()));
  }
  resolveApproval(approvalId: string, req: ResolveApprovalRequest) {
    return unwrap(post<ApprovalResolution>(workflowEndpoints.resolveApproval(approvalId), req));
  }

  // ----- Chain -----
  chainTree(rootInstanceId: string) {
    return unwrap(get<ChainTreeNode>(workflowEndpoints.chainTree(rootInstanceId)));
  }
  descendants(instanceId: string) {
    return unwrap(get<WorkflowInstance[]>(workflowEndpoints.descendants(instanceId)));
  }
  ancestors(instanceId: string) {
    return unwrap(get<WorkflowInstance[]>(workflowEndpoints.ancestors(instanceId)));
  }
}

export const workflowService = new WorkflowService();
export default workflowService;
